import type { LibraryGroup, StoryCtx } from '@dispositio/core';
import cytoscape from 'cytoscape';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { de } from '../../i18n/de';
import { onSchemeChange } from '../../lib/colorScheme';
import { lodFor, type Lod } from '../../lib/canvasModel';
import { entityVNode, GROUP_MIN, membersOf, type PlacedEntity } from '../../lib/libraryModel';
import { readTheme, type Theme } from '../../lib/theme';
import { useAssetVersion } from '../../lib/assets';
import { MAX_ZOOM, MIN_ZOOM, createWheelStepper, fitView, zoomStep, type FitMode } from '../../lib/zoom';
import { buildStyle } from '../canvas/canvasStyle';
import { elementData, remeasureLabels } from '../canvas/StoryCanvas';

export interface LibraryHandle {
  fit: () => void;
  cy: () => cytoscape.Core | null;
  viewportCenter: () => { x: number; y: number };
}

interface Props {
  ctx: StoryCtx;
  placed: PlacedEntity[];
  /** Alle Einträge (auch die im aktuellen Modus ausgeblendeten): Rahmen nehmen beim Verschieben alle mit */
  allPlaced?: PlacedEntity[];
  groups: LibraryGroup[];
  selected: { kind: 'entity' | 'group'; id: string } | null;
  fitTick: number;
  onSelect: (sel: { kind: 'entity' | 'group'; id: string } | null) => void;
  onAddAt: (pos: { x: number; y: number }) => void;
  onMoveEntity: (id: string, pos: { x: number; y: number }) => void;
  /** Rahmen samt der Einträge darin verschoben (die Einträge mit ihrer neuen Lage) */
  onMoveGroup: (id: string, pos: { x: number; y: number }, members: { id: string; x: number; y: number }[]) => void;
  onResizeGroup: (id: string, size: { w: number; h: number }) => void;
  onZoomLevel?: (level: Lod) => void;
  handleRef?: Ref<LibraryHandle>;
}

interface Tip {
  x: number;
  y: number;
  title: string;
  typeName: string;
  text: string;
}

const groupEleId = (id: string): string => `group:${id}`;
const resizeEleId = (id: string): string => `resize:${id}`;
/** Beschriftung der Rahmen bleibt auf dem Bildschirm gleich groß lesbar */
const LABEL_PX = 15;

/** Einpassen; ohne Angabe wie zuletzt (nach „Alles zeigen“ bleibt beim Nachjustieren alles zu sehen) */
const fitCamera = (cy: cytoscape.Core, mode: FitMode = (cy.scratch('_fit') as FitMode | undefined) ?? 'view'): void => {
  cy.scratch('_fit', mode);
  fitView(cy, cy.nodes('.card, .circle, .group'), mode);
};

/**
 * Zeichenfläche der Bibliothek: Personen, Orte und Dinge als Karten frei anordnen (z. B. Orte wie auf einer Karte) und in
 * Rahmen gruppieren. Ein Rahmen nimmt beim Ziehen die Einträge darin mit; an seiner Ecke unten rechts ändert man die Größe.
 */
export function LibraryCanvas(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const sigs = useRef(new Map<string, string>());
  const firstFit = useRef(true);
  const [lod, setLod] = useState<Lod>(1);
  const lodRef = useRef<Lod>(1);
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const [tip, setTip] = useState<Tip | null>(null);
  const assetVersion = useAssetVersion();

  useImperativeHandle(
    props.handleRef,
    () => ({
      fit: () => cyRef.current && fitCamera(cyRef.current, 'all'),
      cy: () => cyRef.current,
      viewportCenter: () => {
        const cy = cyRef.current;
        if (!cy) return { x: 0, y: 0 };
        const pan = cy.pan();
        const z = cy.zoom();
        return { x: Math.round((cy.width() / 2 - pan.x) / z), y: Math.round((cy.height() / 2 - pan.y) / z) };
      },
    }),
    [],
  );

  // --- Initialisierung
  useEffect(() => {
    if (!containerRef.current) return;
    const known = sigs.current;
    const cy = cytoscape({
      container: containerRef.current,
      style: buildStyle(readTheme()),
      userZoomingEnabled: false,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      boxSelectionEnabled: false,
      autounselectify: true,
      pixelRatio: 'auto',
    });
    cyRef.current = cy;
    (window as unknown as { __dpCyLib?: cytoscape.Core }).__dpCyLib = cy; // Test-Hook für E2E

    const relabel = (): void => {
      const fs = LABEL_PX / cy.zoom();
      cy.batch(() =>
        cy.nodes('.group').forEach((n) => {
          if (n.data('fs') !== fs) n.data({ fs, tw: Math.max(Number(n.data('w')) - 20, 40) });
        }),
      );
      // Titel der Rahmen nach neuer Schriftgröße neu ausmessen (sonst schneidet Cytoscape sie auf die alte Fläche zu)
      remeasureLabels(cy.nodes('.group'), fs);
    };
    let raf = 0;
    cy.on('zoom', () => {
      setTip(null);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        relabel();
        const next = lodFor(cy.zoom());
        if (next !== lodRef.current) {
          lodRef.current = next;
          setLod(next);
          propsRef.current.onZoomLevel?.(next);
        }
      });
    });
    cy.on('pan drag', () => setTip(null));

    const stepper = createWheelStepper();
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const dir = stepper(e.deltaY, e.deltaMode);
      if (dir === 0) return;
      const rect = containerRef.current!.getBoundingClientRect();
      zoomStep(cy, dir, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    const host = containerRef.current;
    host.addEventListener('wheel', onWheel, { passive: false });

    // --- Auswahl
    cy.on('tap', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = String(ele.data('role'));
      if (role === 'resize') return;
      propsRef.current.onSelect({ kind: role === 'group' ? 'group' : 'entity', id: String(ele.data('nodeId')) });
    });
    let lastBgTap = 0;
    cy.on('tap', (e) => {
      if (e.target !== cy) return;
      propsRef.current.onSelect(null);
      const now = Date.now();
      if (now - lastBgTap < 350) propsRef.current.onAddAt({ x: Math.round(e.position.x), y: Math.round(e.position.y) });
      lastBgTap = now;
    });

    // --- Ziehen: Einträge frei; Rahmen nehmen die Einträge darin mit; die Ecke ändert die Größe
    let carry: { groupId: string; start: { x: number; y: number }; members: { id: string; ele: cytoscape.NodeSingular | null; x: number; y: number }[] } | null = null;
    const groupOf = (id: string): LibraryGroup | undefined => propsRef.current.groups.find((g) => g.id === id);
    cy.on('grab', 'node.group', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const group = groupOf(String(ele.data('nodeId')));
      if (!group) return;
      propsRef.current.onSelect({ kind: 'group', id: group.id }); // wer einen Rahmen greift, wählt ihn auch
      const members = membersOf(group, propsRef.current.allPlaced ?? propsRef.current.placed).map((m) => {
        const el = cy.getElementById(`entity:${m.entity.id}`);
        return el.nonempty() ? { id: m.entity.id, ele: el, x: el.position().x, y: el.position().y } : { id: m.entity.id, ele: null, x: m.x, y: m.y };
      });
      carry = { groupId: group.id, start: { ...ele.position() }, members };
    });
    cy.on('drag', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = String(ele.data('role'));
      if (role === 'group' && carry) {
        const p = ele.position();
        const dx = p.x - carry.start.x;
        const dy = p.y - carry.start.y;
        for (const m of carry.members) m.ele?.position({ x: m.x + dx, y: m.y + dy });
        const group = groupOf(carry.groupId);
        if (group) cy.getElementById(resizeEleId(group.id)).position({ x: p.x + group.w / 2, y: p.y + group.h / 2 });
      } else if (role === 'resize') {
        const group = groupOf(String(ele.data('nodeId')));
        if (!group) return;
        const p = ele.position();
        const w = Math.max(GROUP_MIN.w, p.x - group.x);
        const h = Math.max(GROUP_MIN.h, p.y - group.y);
        ele.position({ x: group.x + w, y: group.y + h });
        const box = cy.getElementById(groupEleId(group.id));
        box.position({ x: group.x + w / 2, y: group.y + h / 2 });
        box.data({ w, h, tw: Math.max(w - 20, 40) });
      }
    });
    cy.on('dragfree', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = String(ele.data('role'));
      const pr = propsRef.current;
      const p = ele.position();
      if (role === 'card' || role === 'circle') {
        pr.onMoveEntity(String(ele.data('nodeId')), { x: Math.round(p.x), y: Math.round(p.y) });
      } else if (role === 'group' && carry) {
        const group = groupOf(carry.groupId);
        const moved = carry;
        carry = null;
        if (!group) return;
        const dx = p.x - moved.start.x;
        const dy = p.y - moved.start.y;
        pr.onMoveGroup(
          group.id,
          { x: Math.round(group.x + dx), y: Math.round(group.y + dy) },
          moved.members.map((m) => ({ id: m.id, x: Math.round(m.x + dx), y: Math.round(m.y + dy) })),
        );
      } else if (role === 'resize') {
        const group = groupOf(String(ele.data('nodeId')));
        if (!group) return;
        pr.onResizeGroup(group.id, { w: Math.round(Math.max(GROUP_MIN.w, p.x - group.x)), h: Math.round(Math.max(GROUP_MIN.h, p.y - group.y)) });
      }
    });

    // --- Hover
    cy.on('mouseover', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = String(ele.data('role'));
      if (role !== 'card' && role !== 'circle') return;
      const vn = propsRef.current.placed.find((p) => p.entity.id === String(ele.data('nodeId')));
      if (!vn) return;
      const v = entityVNode(propsRef.current.ctx, vn);
      const rp = ele.renderedPosition();
      setTip({ x: rp.x + 16, y: rp.y + 16, title: v.title, typeName: v.typeName, text: v.hover || `@${v.refKey}` });
    });
    cy.on('mouseout', 'node', () => setTip(null));

    // Solange der Nutzer die Ansicht nicht selbst bewegt hat, bleibt sie eingepasst – auch wenn sich die Fläche nach dem Öffnen
    // noch in der Größe ändert (Seitenleiste erscheint, Schriften laden).
    let userMoved = false;
    const markMoved = (): void => {
      userMoved = true;
    };
    containerRef.current.addEventListener('pointerdown', markMoved);
    containerRef.current.addEventListener('wheel', markMoved, { passive: true });
    const ro = new ResizeObserver(() => {
      cy.resize();
      if (!userMoved && cy.nodes().length > 0) fitCamera(cy);
    });
    ro.observe(containerRef.current);
    const onTheme = (): void => {
      cy.style(buildStyle(readTheme()));
      setTheme(readTheme());
    };
    // Theme-Wechsel: System oder Knopf in der Kopfleiste
    const offTheme = onSchemeChange(onTheme);

    return () => {
      cancelAnimationFrame(raf);
      offTheme();
      host.removeEventListener('wheel', onWheel);
      ro.disconnect();
      cy.destroy();
      cyRef.current = null;
      known.clear();
    };
  }, []);

  // --- Elemente synchronisieren
  const { ctx, placed, groups, selected } = props;
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const themeKey = `${theme.surface}|${theme.text}|${theme.border}`;
    const known = sigs.current;
    const wanted = new Map<string, { data: Record<string, unknown>; classes: string; x: number; y: number; grabbable: boolean; sig: string }>();
    for (const g of groups) {
      const fs = LABEL_PX / cy.zoom();
      wanted.set(groupEleId(g.id), {
        data: { nodeId: g.id, role: 'group', w: g.w, h: g.h, color: g.color, bc: g.color, bw: 2, label: g.name, fs, z: 1, tw: Math.max(g.w - 20, 40), img: 'none', opacity: 1, dockTl: '', dockY: 0 },
        classes: 'group',
        x: g.x + g.w / 2,
        y: g.y + g.h / 2,
        grabbable: true,
        sig: JSON.stringify([g.name, g.color, g.w, g.h, themeKey]),
      });
      if (selected?.kind === 'group' && selected.id === g.id) {
        wanted.set(resizeEleId(g.id), {
          data: { nodeId: g.id, role: 'resize', w: 16, h: 16, color: theme.accent, bc: theme.surface, bw: 2, label: '', fs: 10, z: 130, tw: 10, img: 'none', opacity: 1, dockTl: '', dockY: 0 },
          classes: 'resize',
          x: g.x + g.w,
          y: g.y + g.h,
          grabbable: true,
          sig: JSON.stringify(['resize', theme.accent]),
        });
      }
    }
    for (const p of placed) {
      const v = entityVNode(ctx, p);
      const { data, classes } = elementData(v, theme);
      wanted.set(v.id, {
        data: { ...data, nodeId: p.entity.id },
        classes,
        x: p.x,
        y: p.y,
        grabbable: true,
        sig: JSON.stringify([v.title, v.hover, v.typeColor, v.imageId, v.imageId ? assetVersion : 0, v.refKey, lod, themeKey]),
      });
    }
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        if (!wanted.has(n.id())) {
          cy.remove(n);
          known.delete(n.id());
        }
      });
      for (const [id, w] of wanted) {
        const ele = cy.getElementById(id);
        if (ele.empty()) {
          cy.add({ group: 'nodes', data: { id, ...w.data }, position: { x: w.x, y: w.y }, classes: w.classes, grabbable: w.grabbable });
          known.set(id, w.sig);
          continue;
        }
        if (!ele.grabbed()) {
          const p = ele.position();
          if (Math.abs(p.x - w.x) > 0.01 || Math.abs(p.y - w.y) > 0.01) ele.position({ x: w.x, y: w.y });
        }
        if (known.get(id) !== w.sig) {
          ele.data(w.data);
          ele.classes(w.classes);
          ele.removeScratch('_measuredFs'); // Titel neu ausmessen
          known.set(id, w.sig);
        }
      }
    });
    remeasureLabels(cy.nodes('.group'), LABEL_PX / cy.zoom());
    if (firstFit.current && wanted.size > 0) {
      firstFit.current = false;
      fitCamera(cy);
      const level = lodFor(cy.zoom());
      if (level !== lodRef.current) {
        lodRef.current = level;
        setLod(level);
        props.onZoomLevel?.(level);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, placed, groups, selected, lod, theme, assetVersion]);

  // --- Auswahl hervorheben
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.elements().removeClass('sel');
      if (selected) cy.nodes().filter((n) => n.data('nodeId') === selected.id && n.data('role') !== 'resize').addClass('sel');
    });
  }, [selected, placed, groups]);

  useEffect(() => {
    const cy = cyRef.current;
    // fitTick kommt nur von „Alles zeigen“
    if (cy && props.fitTick > 0) fitCamera(cy, 'all');
  }, [props.fitTick]);

  return (
    <div className="canvas-host">
      <div ref={containerRef} className="canvas" data-testid="library-canvas" />
      {tip && (
        <div className="tooltip" style={{ left: tip.x, top: tip.y }} role="tooltip">
          <div className="t-title">{tip.title}</div>
          <div className="muted small">{tip.typeName}</div>
          <div>{tip.text}</div>
        </div>
      )}
      <div className="canvas-overlay legend" aria-label="Zoomstufe">
        <span className="muted" data-testid="library-zoom-level">
          {de.library.zoom[lod]}
        </span>
      </div>
    </div>
  );
}
